import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, BRANCH_KEY, TOKEN_KEY, exitImpersonation, getToken, impToken, setBranch } from "@/lib/api";
import { isOnline, onConnectivity } from "@/lib/connectivity";
import { cachedSession, cacheSession, jwtSub } from "@/lib/localdb";
import { rememberLogin } from "@/lib/offlineAuth";
import { syncNow } from "@/lib/outbox";

const AuthCtx = createContext(null);
const DISABLED = "This account is disabled";
const OFFLINE_USER = "samrtin_offline_user";

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null); // null=checking, false=anon, object=authed
  const offlineCreds = useRef(null); // in memory only, to re-authenticate after an offline login

  const endSession = useCallback(async (msg) => {
    localStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(OFFLINE_USER);
    offlineCreds.current = null;
    setSession(false);
    if (msg) toast.error(msg, { id: "session-ended" });
  }, []);

  const signIn = useCallback((data, creds) => {
    sessionStorage.removeItem(OFFLINE_USER);
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(BRANCH_KEY, data.branch.id);
    const { token, ...s } = data;
    setSession(s);
    cacheSession(s).catch(() => {});
    if (creds) rememberLogin(creds.identifier, creds.password, data).catch(() => {});
  }, []);

  // After an offline login there is no token: sign in again with the in-memory credentials once online.
  const reLogin = useCallback(async () => {
    const c = offlineCreds.current;
    try {
      const { data } = await api.post("/auth/login", { identifier: c.identifier, password: c.password });
      offlineCreds.current = null;
      signIn(data, c);
    } catch (e) {
      if (!e.response) return;
      const msg = e.response.data?.detail;
      if (msg === DISABLED) {
        const r = await api.post("/auth/login", { identifier: c.identifier, password: c.password, sync_only: true }).catch(() => null);
        if (r?.data?.sync_token) await syncNow(r.data.sync_token).catch(() => {});
      }
      endSession(msg === DISABLED ? DISABLED : "Please log in again");
    }
  }, [signIn, endSession]);

  const refresh = useCallback(async () => {
    const token = getToken();
    if (!token) {
      if (offlineCreds.current) return isOnline() && reLogin();
      const offUser = sessionStorage.getItem(OFFLINE_USER);
      const cached = offUser && !isOnline() ? await cachedSession(offUser) : null;
      if (cached) return setSession({ ...cached, offline: true, offlineLogin: true });
      if (offUser && isOnline()) {
        sessionStorage.removeItem(OFFLINE_USER);
        return endSession("Please log in again");
      }
      return setSession(false);
    }
    try {
      const { data } = await api.get("/auth/me");
      setBranch(data.branch.id);
      setSession(data);
      if (!data.impersonation) cacheSession(data).catch(() => {});
    } catch (e) {
      if (impToken() && e?.response?.status === 401) return exitImpersonation();
      if (!e?.response) {
        if (impToken()) return;
        const cached = await cachedSession(jwtSub(token));
        setSession((s) => (s ? { ...s, offline: true } : cached ? { ...cached, offline: true } : false));
        return;
      }
      if (e.response.status === 401 || e.response.status === 403) {
        await syncNow().catch(() => {});
        const msg = e.response.data?.detail;
        endSession(msg === DISABLED ? DISABLED : null);
      }
    }
  }, [reLogin, endSession]);

  useEffect(() => {
    refresh();
    const onFocus = () => document.visibilityState === "visible" && refresh();
    const iv = setInterval(onFocus, 30000);
    const offConn = onConnectivity((up) => up && refresh());
    window.addEventListener("demo-expired", refresh);
    window.addEventListener("session-stale", refresh);
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(iv);
      offConn();
      window.removeEventListener("demo-expired", refresh);
      window.removeEventListener("session-stale", refresh);
      window.removeEventListener("focus", onFocus);
    };
  }, [refresh]);

  const signInOffline = (cached, creds) => {
    offlineCreds.current = creds;
    sessionStorage.setItem(OFFLINE_USER, cached.user.id);
    localStorage.setItem(BRANCH_KEY, cached.branch.id);
    setSession({ ...cached, offline: true, offlineLogin: true });
  };
  const signOut = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(BRANCH_KEY);
    sessionStorage.removeItem(OFFLINE_USER);
    offlineCreds.current = null;
    setSession(false);
  };
  const setBusiness = (business) => setSession((s) => ({ ...s, business }));
  const switchBranch = async (id) => {
    setBranch(id);
    await refresh();
  };

  return <AuthCtx.Provider value={{ session, signIn, signInOffline, signOut, setBusiness, refresh, switchBranch }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
export const useCan = () => {
  const { session } = useAuth();
  return (perm) => !!session?.permissions?.[perm];
};

// Offline the cached demo expiry is checked against the device clock.
export const isLocked = (s) => !s.impersonation && (!!s.business.demo?.expired || (!!s.offline && s.business.plan === "demo" && Date.parse(s.business.demo_expires_at) <= Date.now()));
